import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

interface HoverCardProps {
  children: React.ReactNode
  content: React.ReactNode
  className?: string
  contentClassName?: string
  disabled?: boolean
}

/**
 * A hover card component that shows content on hover.
 * Uses a portal to escape overflow:hidden on parent elements.
 */
export function HoverCard({
  children,
  content,
  className,
  contentClassName,
  disabled = false,
}: HoverCardProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [position, setPosition] = React.useState({ top: 0, left: 0 })
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const updatePosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      // Position above the trigger, aligned to the right edge
      setPosition({
        top: rect.top - 8, // 8px above the trigger
        left: rect.right, // Align to right edge of trigger
      })
    }
  }, [])

  const handleMouseEnter = () => {
    if (disabled) return
    updatePosition()
    // Small delay to prevent accidental triggers
    timeoutRef.current = setTimeout(() => setIsOpen(true), 150)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsOpen(false)
  }

  return (
    <div
      ref={triggerRef}
      className={cn('relative inline-block', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isOpen && content && createPortal(
        <div
          className={cn(
            'fixed z-[9999] w-80 max-w-sm rounded-md border bg-white p-3 text-sm shadow-lg',
            'animate-in fade-in-0 zoom-in-95 duration-150',
            contentClassName
          )}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translate(-100%, -100%)', // Position above and to the left of the point
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => {
            // Keep tooltip open when hovering over it
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
          }}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </div>,
        document.body
      )}
    </div>
  )
}

/**
 * A pre-styled hover card for showing call transcripts/summaries
 */
interface CallPreviewHoverCardProps {
  summary?: string | null
  transcript?: string | null
  children: React.ReactNode
  className?: string
}

export function CallPreviewHoverCard({
  summary,
  transcript,
  children,
  className,
}: CallPreviewHoverCardProps) {
  // Use summary if available, fall back to transcript
  const previewContent = summary || transcript

  if (!previewContent) {
    return <>{children}</>
  }

  // Truncate long content
  const truncatedContent = previewContent.length > 300
    ? previewContent.substring(0, 300) + '...'
    : previewContent

  return (
    <HoverCard
      className={className}
      content={
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {summary ? 'Call Summary' : 'Transcript Preview'}
          </div>
          <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
            {truncatedContent}
          </p>
          {previewContent.length > 300 && (
            <p className="text-xs text-gray-400 italic">
              Click row to see full {summary ? 'summary' : 'transcript'}
            </p>
          )}
        </div>
      }
    >
      {children}
    </HoverCard>
  )
}
